import { useContext, useEffect, useState } from "react";
import { useAppSelector } from "../../redux/store.ts";
import Button from "../../shared/button/Button.tsx";
import { BusinessModels, CompanyTiers } from "../User/constants.ts";
import { getSubscriptionsList } from "../../api/companyApi.ts";
import { twJoin } from "tailwind-merge";
import { DotOutlineIcon } from "@phosphor-icons/react/dist/icons/DotOutline";
import { handleBookCall } from "../../api/ApiHelpers.ts";
import { GlobalOverlayContext } from "../../context/OverlayContext.tsx";
import { ModalType } from "../../lib/constants/Overlays.ts";
import TopupForm from "../Billings/Wallet/components/TopupForm.tsx";

const TrialExpired = ({ slug }) => {
   const { openModal } = useContext(GlobalOverlayContext);

  const user = useAppSelector((store) => store.user.user);
  const [pricingPlans, setPricingPlans] = useState([]);
  const heading =
    user?.business_model == BusinessModels.Credit &&
    ((user?.company_tier as string) ?? "").trim().toLowerCase() !=
      CompanyTiers.Paid
      ? "Oops! Your free trial has ended"
      : "Oops! Your credits have expired.";
  const description =
    user?.company_tier == CompanyTiers.Free ? (
      <>
        Ready to keep exploring all our features? <br />
        Just reach out to our sales team and we’ll help you find the perfect
        plan.
      </>
    ) : (
      <>
        {" "}
        Ready to explore all our features? <br />
        Just reach out to our sales team and we’ll help you find the perfect
        plan.
      </>
    );

  useEffect(() => {
    getPricingPlans();
  }, []);

  const getPricingPlans = async () => {
    const res = await getSubscriptionsList(slug);
    if (res.status)
      setPricingPlans(
        res.data
          ?.filter((plan) => plan?.duration_days > 30)
          .sort(
            (a, b) =>
              a?.additional_metadata?.order - b?.additional_metadata?.order
          )
      );
  };

  const openTopupModal = () => {
    openModal({
      type: ModalType.CUSTOM,
      props: {
        title: "Topup Credits",
        children: <TopupForm slug={slug} />,
        className: "sm:w-96",
      },
    });
  };

  return (
    <div className="relative inset-0 flex h-full w-full flex-col items-center justify-center gap-6 overflow-hidden px-4">
      <h1 className="text-center text-2xl font-normal text-gray-250">
        {heading}
      </h1>
      <p className="text-center text-base text-gray-300">{description}</p>
      <div className="relative mt-6 flex h-full w-full grow justify-between gap-6 xl:gap-16">
        {pricingPlans?.map((plan, i) => (
          <div
            key={plan?.id}
            className={twJoin(
              "relative flex h-full w-full flex-col rounded-xl p-px hover:shadow-[0_0_0.5rem_0.25rem]",
              plan?.flat_fee > 0
                ? "bg-gradient-to-r from-pink-600 to-app-500 hover:shadow-app-400/40"
                : "bg-gray-350 hover:bg-yellow-400 hover:shadow-yellow-400/40"
            )}
          >
            {plan?.flat_fee > 0 && (
              <h4 className="px-4 py-1 text-center text-sm font-light uppercase tracking-widest">
                Pay as you go
              </h4>
            )}
            <div
              className={`relative z-10 flex w-full grow flex-col gap-6 rounded-xl border border-gray-600 p-6 ${plan?.flat_fee == 0 ? "pt-[3.25rem]" : "rounded-t"} ${plan?.additional_metadata?.is_recommended == "true" ? "bg-app-950" : "bg-gray-900"} `}
            >
              <div className="border-b border-gray-500 pb-4">
                <h2 className="text-xl font-normal text-gray-250">
                  {plan?.additional_metadata?.subtitle ||
                    plan?.additional_metadata?.title}
                </h2>
              </div>
              <div className="w-full grow text-sm text-gray-250">
                {plan?.description && (
                  <p className="mb-4 text-sm italic text-gray-350">
                    {plan?.description}
                  </p>
                )}
                {plan?.additional_metadata?.description?.map((desc) => (
                  <p className="mb-1.5">
                    <DotOutlineIcon
                      weight="fill"
                      size={20}
                      className="mr-2 inline-block fill-app-400"
                    />{" "}
                    {desc}
                  </p>
                ))}
              </div>
              <div className="flex flex-col items-center justify-center gap-4 border-t border-gray-500 pt-4 text-center">
                <h3 className="text-base font-medium text-gray-200">
                  {plan?.additional_metadata?.price}
                </h3>
                <p className="min-h-4 text-xs text-gray-250">
                  {plan?.additional_metadata?.credits}
                </p>
                {user?.is_edit_admin ? (
                  <Button onClick={openTopupModal} className="shrink-0" size="sm">
                  Buy more credits
                </Button>
                ) : (
                  <Button onClick={() => handleBookCall} className="w-full">
                    Talk to Us
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrialExpired;
